import React, { useEffect, useState } from 'react'
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { Language, LanguageDictionary, LocaleLanguages, LocaleNames } from './language'
import { setFormatLocale } from './format'
import { LanguageResources, getLanguageResources, LanguageContext, UserParticipantContext, DropdownContext, AnonParticipant, EmptyDropdownData, UserInfo } from './contexts'
import { DropdownData } from './Models/system'
import { Workshop } from './Models/workshops'
import { WorkshopMenuItem } from './Models/workshopMenu'
import { API, getWorkshopParametersFromQueryString } from './http'

import { WorkshopEvents } from './WorkshopEvents/WorkshopEvents'
import { AppView } from './AppView';
import { Participant, RelatedParticipant } from './Models/participant';
import { getUpdatedWorkshops, paddMenuItems } from './Models/logic';
import { useSessionCheck } from './Hooks/SessionCheck';
import { CardModal } from './Components/Modal';
import { Config, SpecialClassNames } from './constants';
import classNames from 'classnames';
import { NoWorkshopEventsAvailable } from './Pages/NoWorkshopEventsAvailable';
import PageNotFound from './Pages/PageNotFound'
import { MenuItemPage } from './Pages/MenuItemPage'
import WorkshopNotFound from './Pages/WorkshopNotFound'
import HelpPageSample from './Pages/HelpPageSample'
import { ImageBanner } from './Components/ImageBanner'

function getPreferredLanguage(): Language {
	const localeName = UserInfo.getUserLocale() 

	if (localeName) return LocaleLanguages[localeName]
	else return 'English'
}

function setPreferredLanguage(language: Language) {
  UserInfo.setUserLocale(LocaleNames[language])

	API.setPreferredLanguage(language)
}

function App() {

  const [sessionStatus, extendSession] = useSessionCheck()

  // Data pulled from api
  const [workshopData, setWorkshopData] = useState<Array<Workshop>>([])
  const [activeWorkshops, setActiveWorkshops] = useState<Array<Workshop>>([])
  const [workshopMenuItems, setWorkshopMenuItems] = useState<Array<WorkshopMenuItem>>([])
  const [dropdownData, setDropdownData] = useState<LanguageDictionary<DropdownData>>({ English: EmptyDropdownData, French: EmptyDropdownData })
  const [localizedDropdownData, setLocalizedDropdownData] = useState(EmptyDropdownData)
  const [loadComplete, setLoadComplete] = useState(false)

  const [languageResources, setLanguageResources] = useState<LanguageResources>(getLanguageResources(getPreferredLanguage()))

    // TODO: may require React.useCallback here
  function setLanguage(language: Language){
    const languageResources = getLanguageResources(language, setLanguage)
    setFormatLocale(LocaleNames[language])
    setLanguageResources(languageResources)
    setLocalizedDropdownData(dropdownData[language])
    setPreferredLanguage(language)
  }

  const [participant, setParticipant] = useState(AnonParticipant)

  // registering participants automatically extends the session at the server end
  function registerParticipant(participant: Participant, additionalParticipant: RelatedParticipant | undefined, workshopEventId: number) {
    return API.registerParticipant(participant, additionalParticipant, workshopEventId).then(async response => {
      if (response.Status == 'success') {

        const updatedWorkshops = getUpdatedWorkshops(workshopData, 'Register', workshopEventId, response.Data.SeatsLeft, response.Data.MaximumParticipants)
        let updatedParticipant: Participant

        if (additionalParticipant && additionalParticipant.RegistrationId <= 0) {
          // We've added a new guest, and we don't have their newly minted registrationId.  
          // Let's refresh user participant info to get an updated related participant list.
            updatedParticipant = await API.getParticipant(UserInfo.getUserId()!)
        }
        else updatedParticipant = participant

        // update participant and event state
        setParticipant(updatedParticipant)
        setWorkshopData(updatedWorkshops)
      }
      return response
    })
  }

  // deregistering participants automatically extends the session at the server end
  function deregisterParticipant(workshopEventId: number) {
    return API.deregisterParticipant(workshopEventId).then(response => {
      if (response.Status == 'success') {

        const updatedWorkshops = getUpdatedWorkshops(workshopData, 'Deregister', workshopEventId, response.Data.SeatsLeft, response.Data.MaximumParticipants)

          // update event state
          setWorkshopData(updatedWorkshops)
      }
      return response
    })
  }

  async function refreshWorkshops() {
    const newWorkshops = await API.getWorkshopData()
    setWorkshopData(newWorkshops)
  }

  const navigate = useNavigate()

  const location = useLocation()
  const pathParts = location.pathname.split('/')
  const lastPathPart = pathParts[pathParts.length - 1]
  let selectedPage = lastPathPart 
  const selectedWorkshopId = Number.parseInt(lastPathPart)

  // Routing hack here.  Clean this up so that the router takes care of this
  if (Number.isNaN(selectedWorkshopId) && selectedPage == '') {
    selectedPage = '/'
  }

  useEffect(() => {
    // we refresh the event list if the user moves to another page
    refreshWorkshops()

    // getting workshop data is a public api
    // we manually extend the session here so that moving to another page extends it
    extendSession()
  }, [lastPathPart])

  // query string data
  const [workshopEventIdFromQueryString, setWorkshopEventIdFromQueryString] = useState<number>()

  const selectedWorkshop = workshopData.find(w => w.WorkshopId == selectedWorkshopId)
  const selectedPageMenuItem = workshopMenuItems.find(mi => mi.page == selectedPage)

  let mainContent: React.ReactElement | null = null
  let renderLandingImage = true

  // this is kindof outside in.  
  // TODO: rewrite so that routing takes care of getting correct workshop, no workshops available, or 404
  if (loadComplete && !selectedPageMenuItem) {
    if (selectedWorkshop) {
      renderLandingImage = false
      mainContent = <WorkshopEvents workshop={selectedWorkshop} workshopEventIdFromQueryString={workshopEventIdFromQueryString} />
    }
    else if (selectedWorkshopId > 0) {
      let activeWorkshop = activeWorkshops.find(ws => ws.WorkshopId == selectedWorkshopId)

      if (activeWorkshop) mainContent = <NoWorkshopEventsAvailable workshop={activeWorkshop} />
      else mainContent = <WorkshopNotFound workshopId={selectedWorkshopId} />
    }
    else {
      // invalid route here
      mainContent = <PageNotFound />
    }
  }

  // pull workshops, menu, and location tier items from api
  useEffect(() => {
    const publicUserId = UserInfo.getUserId()
    if (publicUserId) {
      API.getParticipant(publicUserId).then(participant => {
        setParticipant(participant)
      })
    }
    else setParticipant(AnonParticipant)

    Promise.all([API.getWorkshopMenu(), API.getWorkshopData(), API.getActiveWorkshops(), API.getDropdownData('English'), API.getDropdownData('French')])
      .then(([workshopMenuItems, workshopData, activeWorkshops, English, French]) => {
        // pad out the menu with new workshops if they haven't been configured in the menu editor yet
        let paddedMenu = paddMenuItems(workshopMenuItems, workshopData)
        setWorkshopMenuItems(paddedMenu)
        setWorkshopData(workshopData)
        setActiveWorkshops(activeWorkshops)

        const dropdownData = { English, French }
        setDropdownData(dropdownData)
        setLocalizedDropdownData(dropdownData[languageResources.language])

        setLoadComplete(true)

        let queryStringData = getWorkshopParametersFromQueryString(workshopData)
        if (queryStringData.workshopId) {
          const {workshopEventId, workshopId} = queryStringData
          navigate(`/${workshopId}?workshopEventId=${workshopEventId}`)
          setWorkshopEventIdFromQueryString(workshopEventId)
        }
    })
  }, [])

  document.title = languageResources.localizedText.Workshops

  const {localizedText} = languageResources
  const extendSessionModal = sessionStatus.status == 'ExpiringSoon' ? 
    <CardModal  title={localizedText.SessionExpirationTitle} 
            onCancel={UserInfo.logUserOut} 
            primaryButton={{text: localizedText.Continue, color: 'primary', onClick: extendSession}} 
            className={classNames(SpecialClassNames.narrowModal)}
    >
        {localizedText.SessionExpirationWarning}
    </CardModal> : null

  const staticRoutes = workshopMenuItems
    .filter(menuItem => !!menuItem.page)
    .map((menuItem, menuItemIndex) => { 

      let imageBanner: React.ReactElement | null = null

      if (menuItem.page && Config.StaticPagesWithBanner.indexOf(menuItem.page) >= 0) {
        imageBanner = <ImageBanner language={languageResources.language} />
      }

      const element = <MenuItemPage menuItem={menuItem} language={languageResources.language}>{imageBanner}</MenuItemPage>

      return <Route key={menuItemIndex} path={menuItem.page!} element={element} />
    })


  return (
    <LanguageContext.Provider value={{ ...languageResources, setLanguage }}>
      <UserParticipantContext.Provider value={{ participant, registerParticipant, deregisterParticipant, refreshWorkshops }}>
        <DropdownContext.Provider value={localizedDropdownData}>
          <AppView 
            renderLandingImage={renderLandingImage}
            workshopMenuItems={workshopMenuItems} 
            language={languageResources.language}>
              <Routes>
                {staticRoutes}
                <Route path={'/helpsample'} element={<HelpPageSample />}></Route>
                <Route path={'/:workshopId'} element={mainContent}></Route>
              </Routes>
            {extendSessionModal}
          </AppView>
        </DropdownContext.Provider>
      </UserParticipantContext.Provider>
    </LanguageContext.Provider>
  )
}

export default App
